<script>
import moment from 'moment';
import AdditionalVisitorsService from '@/services/AdditionalVisitorsService';
import AdditionalVisitorSafeguardingForm
  from '@/components/additionalVisitors/details/sections/AdditionalVisitorSafeguardingForm.vue';

export default {
  name: 'AdditionalVisitorDetails',
  components: { AdditionalVisitorSafeguardingForm },
  data() {
    return {
      loading: true,
      additionalVisitor: null,
      safeguardingStatus: null,
      safeguardingHistory: [],
      safeguardingFields: [
        { label: 'Officer name', key: 'officerName' },
        { label: 'Status', key: 'status' },
        { label: 'Notes', key: 'notes' },
        { label: 'Date', key: 'date' },
      ],
      currentPage: 1,
      perPage: 1,
      totalCount: 0,
      safeguardingModalId: 'modal-safeguarding',
    };
  },
  async mounted() {
    this.additionalVisitor = this.$store.state.participants.participant;
    try {
      await Promise.all([
        this.getAdditionalVisitor(),
        this.getSafeguardingHistory(),
      ])
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getAdditionalVisitor() {
      try {
        await this.$store.dispatch('participants/getParticipant', this.additionalVisitor.additional_visitor_id);
        const additionalVisitorsStore = this.$store.state.participants;
        this.additionalVisitor = additionalVisitorsStore.participant;
        this.safeguardingStatus = additionalVisitorsStore.safeguardingStatus;
      } catch (e) {
        console.error('Error getting additional visitor: ', e);
        this.$toast.error('Error getting additional visitor');
        throw e;
      }
    },
    async getSafeguardingHistory() {
      try {
        const response = await AdditionalVisitorsService.getAdditionalVisitorsSafeguardingHistory(this.additionalVisitor.additional_visitor_id);
        this.safeguardingHistory = response.data;
        this.safeguardingHistory.sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
        this.totalCount = this.safeguardingHistory.length;
      } catch (e) {
        console.error('Error getting safeguarding history: ', e);
        this.$toast.error('Error getting additional visitor');
        throw e;
      }
    },
    formatDobAndAge() {
      const { dob } = this.additionalVisitor;
      return `${moment(dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(dob, 'years')})`;
    },
    isUnder18() {
      const { dob } = this.additionalVisitor;
      return parseInt(moment(new Date()).diff(dob, 'years'), 10) < 18;
    },
    showModal(name) {
      this.$bvModal.show(name);
    },
    closeModals(refresh = false) {
      this.$bvModal.hide(this.safeguardingModalId);
      if (refresh) {
        this.getAdditionalVisitor();
        this.getSafeguardingHistory();
      }
    },
  },
}
</script>

<template>
  <div v-if="loading">
    <div class="col text-center mt-5">
      <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
    </div>
  </div>
  <div v-else>
    <b-card>
      <b-row>
        <b-col cols="6">
          <b-card-title>
            Additional Visitor Details
            <b-badge
              v-if="isUnder18"
              :variant="additionalVisitor.safeguarded_at_prison ? 'primary' : 'danger'"
              class="float-right"
            >
              Under 18
            </b-badge>
          </b-card-title>
          <div class="detail-item">
            Name:
            <b>{{ additionalVisitor.firstname }} {{ additionalVisitor.lastname }}</b>
          </div>
          <div class="detail-item">
            D.O.B. & age:
            <b>{{ formatDobAndAge(additionalVisitor.dob) }}</b>
          </div>
          <div class="detail-item">
            Address line 1:
            <b>{{ additionalVisitor.address.address1 }}</b>
          </div>
          <div class="detail-item">
            Address line 2:
            <b>{{ additionalVisitor.address.address2 }}</b>
          </div>
          <div v-if="additionalVisitor.address.address3" class="detail-item">
            Address line 3:
            <b>{{ additionalVisitor.address.address3 }}</b>
          </div>
          <div class="detail-item">
            City/Town:
            <b>{{ additionalVisitor.address.city_town }}</b>
          </div>
          <div class="detail-item">
            Region:
            <b>{{ additionalVisitor.address.region }}</b>
          </div>
          <div class="detail-item">
            Postcode:
            <b>{{ additionalVisitor.address.zip_postal_code }}</b>
          </div>
          <div class="detail-item">
            Country code:
            <b>{{ additionalVisitor.address.country }}</b>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-card no-body>
      <b-card-title class="pt-2 px-2">
        Safeguarding History
        <b-btn
          v-if="!additionalVisitor.safeguarded_at_prison"
          class="float-right"
          size="sm"
          variant="primary"
          @click="showModal(safeguardingModalId)"
        >
          Complete Safeguarding
        </b-btn>
        <b-btn
          v-else
          class="float-right"
          size="sm"
          variant="danger"
          @click="showModal(safeguardingModalId)"
        >
          Revoke Safeguarding
        </b-btn>
      </b-card-title>
      <b-row>
        <b-col>
          <b-table
            :fields="safeguardingFields"
            :items="safeguardingHistory"
            class="data-table mb-0"
            hover
            responsive
            show-empty
          >
            <template #cell(officerName)="data">
              {{ data.item.prison_officer_name }}
            </template>
            <template #cell(status)="data">
              <b-badge
                class="badge-pill badge-primary"
                :variant="data.item.status ? 'primary' : 'danger'"
              >
                {{ data.item.status ? 'Completed' : 'Revoked' }}
              </b-badge>
            </template>
            <template #cell(notes)="data">
              {{ data.item.notes }}
            </template>
            <template #cell(date)="data">
              {{ data.item.created_at | formatDateTime }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card>

    <b-modal :id="safeguardingModalId" hide-footer>
      <additional-visitor-safeguarding-form
        :completing_safe_guarding="!additionalVisitor.safeguarded_at_prison"
        @close="closeModals()"
        @refresh="closeModals(true)"
      />
    </b-modal>
  </div>
</template>

<style scoped>
.detail-item {
  display: flex;
  justify-content: space-between;
}
</style>
